import styled from "styled-components";
import { useMemo } from "react";
import { useQuery } from "@apollo/client";

import { JobLocationsDocument, Location } from "@hire/schema";
import { palette, modularScale, pxToRem } from "@otta/design-tokens";
import { SelectField, Text, VerticalSpacing, media } from "@otta/design";
import { Radio } from "@hire/components/input/Radio";
import {
  locationToLocationOption,
  stringToLocation,
} from "@hire/util/locations";
import { hireAppUser } from "@hire/util/user";

const FilterContainer = styled.div`
  z-index: 1;
  display: flex;
  flex-direction: column;
  padding: ${modularScale(2)} ${modularScale(0)};
  min-width: ${pxToRem(264)};
  background-color: ${palette.brand.white};
  border-right: 1px solid ${palette.grayscale.shade200};
  ${media({ from: "tablet" })`
    height: 100%;
  `}
  ${media({ to: "tablet" })`
    border-right: none;
    border-bottom: 1px solid ${palette.grayscale.shade200};
`}
`;

const RadioContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${pxToRem(16)};
  ${media({ from: "tablet" })`
    flex-direction: column;
  `}
`;

interface SearchParams {
  status?: string | undefined;
  locations?: Location[] | undefined;
}

interface FiltersProps {
  status: string;
  location?: Location | null;
  updateSearchParams: (params: SearchParams) => void; //({ status, locations }: SearchParams) => void;
}

interface IOption {
  label: string;
  value: string;
}

const defaultLocation = { label: "All", value: null };

export const Filters = ({
  status,
  location = null,
  updateSearchParams,
}: FiltersProps): React.ReactElement => {
  const { data, loading } = useQuery(JobLocationsDocument);

  const locationOptions = useMemo(() => {
    const options =
      hireAppUser(data?.me)
        ?.currentCompany?.jobLocations.map(l => locationToLocationOption(l))
        .filter((l): l is IOption => !!l) ?? [];

    return [defaultLocation, ...options];
  }, [data?.me]);

  return (
    <FilterContainer>
      <VerticalSpacing size={2}>
        <Text bold size={1}>
          Filter
        </Text>
        <VerticalSpacing>
          <Text as="label" bold htmlFor="location-select">
            Location
          </Text>

          <SelectField
            inputId="location-select"
            options={locationOptions}
            defaultValue={
              locationOptions.find(option => option.value === location) ??
              defaultLocation
            }
            isLoading={loading}
            onChange={v => {
              const location = v?.value ? stringToLocation(v?.value) : null;

              if (location) {
                updateSearchParams({ locations: [location] });
              } else {
                updateSearchParams({ locations: [] });
              }
            }}
          />
        </VerticalSpacing>

        <VerticalSpacing>
          <Text bold>Status</Text>
          <RadioContainer>
            <Radio
              label="Published"
              name="published-radio-input"
              checked={status === "published"}
              onChange={() => updateSearchParams({ status: "published" })}
            />
            <Radio
              label="Review"
              name="draft-radio-input"
              checked={status === "review"}
              onChange={() => updateSearchParams({ status: "review" })}
            />
            <Radio
              label="Draft"
              name="draft-radio-input"
              checked={status === "draft"}
              onChange={() => updateSearchParams({ status: "draft" })}
            />
            <Radio
              label="Archived"
              name="archived-radio-input"
              checked={status === "archived"}
              onChange={() => updateSearchParams({ status: "archived" })}
            />
          </RadioContainer>
        </VerticalSpacing>
      </VerticalSpacing>
    </FilterContainer>
  );
};
